"use client";

import { Loader2, ScanLine } from "lucide-react";
import { useRouter } from "next/navigation";
import { lazy, Suspense, useCallback, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useScanPass } from "./use-scan-pass";

// loaded when first shown: the viewer pulls in the translation client, which the module barrel must not load at import
const PdfPreviewButton = lazy(() => import("@/components/documents/pdf-preview-modal").then((m) => ({ default: m.PdfPreviewButton })));

type Texts = { scan: string; waiting: string; waitingHelp: string; abandon: string; failed: string; expired: string; printed: string; fullCode: string; key: string; sameCode: string; keepCode: string; duplicate: string; view: string };
type Existing = { id: string; category: string; day: string; version: number; mime: string };

function ExistingFiles({ existing, texts, onKeep, onDuplicate }: { existing: Existing[]; texts: Texts; onKeep: () => void; onDuplicate: () => void }) {
  return (
    <div className="flex flex-col gap-2 rounded-md border p-2 text-sm">
      <p>{texts.sameCode}</p>
      <ul>{existing.map((f) => <li key={f.id} className="flex items-center justify-between gap-2"><span>{f.day} {f.category} V{String(f.version).padStart(3, "0")}</span>{f.mime === "application/pdf" ? <Suspense fallback={null}><PdfPreviewButton href={`/api/documents/explorer/file/${f.id}?inline=1`} title={f.category}>{texts.view}</PdfPreviewButton></Suspense> : <a href={`/api/documents/explorer/file/${f.id}`} className="underline" download>{texts.view}</a>}</li>)}</ul>
      <div className="flex gap-2"><Button type="button" size="sm" variant="outline" onClick={onKeep}>{texts.keepCode}</Button><Button type="button" size="sm" variant="outline" onClick={onDuplicate}>{texts.duplicate}</Button></div>
    </div>
  );
}

/** A button that scans a document with SAADS Scan into Files for the owner `ref` of the scan target `kind`. It blocks the page with "waiting for the scan" until the app sends the file (then the page refreshes where it is) or the person abandons. */
export function ScanButton({ kind, reference, label, texts, children, onDone }: { kind: string; reference: string; label: string; texts: Texts; children?: ReactNode; onDone?: () => void }) {
  const router = useRouter();
  const done = useCallback(() => (onDone ? onDone() : router.refresh()), [router, onDone]);
  const [asking, setAsking] = useState(false);
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"last4" | "full" | "key">("last4");
  const [key, setKey] = useState("");
  const [existing, setExisting] = useState<Existing[]>([]);
  const form = useRef<HTMLFormElement>(null);
  const { pass, problem, scan, abandon } = useScanPass(kind, reference, label, done);
  /** Starts the scan: `redact` = new code and the old one whited out; `asCode` = kept under that code; neither = as printed when a code was typed. */
  function go(redact: boolean, asCode: string) {
    const answers = children && form.current ? btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(Object.fromEntries(new FormData(form.current)))))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "") : "";
    setAsking(false);
    setExisting([]);
    void scan(!children && code.trim() !== "" && !redact && !asCode, answers, redact, asCode);
  }
  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={() => setAsking(true)}><ScanLine aria-hidden />{texts.scan}</Button>
      {problem && <p role="alert" className="text-sm text-destructive">{texts[problem]}</p>}
      <Dialog open={asking && pass === null} onOpenChange={setAsking}>
        <DialogContent className="sm:max-w-sm">
          <DialogTitle>{label}</DialogTitle>
          {/* what the page asks before scanning (title, category, expiry...): the fields are children, sent with the pass as base64url JSON */}
          <form ref={form} className="flex flex-col gap-3" onSubmit={(e) => e.preventDefault()}>
            {children}
            {!children && <label className="flex flex-col gap-1 text-sm">{step === "last4" ? texts.printed : texts.fullCode}<input value={code} onChange={(e) => { setCode(e.target.value); setStep(e.target.value.trim().length > 4 ? "full" : "last4"); }} className="rounded-md border bg-background px-2 py-1 font-mono" autoComplete="off" /></label>}
            {!children && step === "key" && <label className="flex flex-col gap-1 text-sm">{texts.key}<input value={key} onChange={(e) => setKey(e.target.value)} className="rounded-md border bg-background px-2 py-1 font-mono" autoComplete="off" /></label>}
            {existing.length > 0 && <ExistingFiles existing={existing} texts={texts} onKeep={() => go(false, code.trim().toUpperCase())} onDuplicate={() => go(true, "")} />}
            <Button type="button" onClick={async () => {
              if (form.current && !form.current.reportValidity()) return;
              const typed = code.trim();
              if (!children && typed) {
                const check = (extra: object) => fetch("/api/documents/scan/check-code", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ kind, ref: reference, code: typed, ...extra }) }).then((r) => r.json()).catch(() => null) as Promise<{ match?: boolean; existing?: Existing[] } | null>;
                if (step === "key") {
                  const res = await check({ key: key.trim() });
                  if (!res?.match) return go(true, ""); // unknown code or wrong key: scan anyway as a new document and white out the old code
                  if (res.existing?.length) return setExisting(res.existing); // another file holds this code: look at it first
                  return go(false, typed.toUpperCase()); // the paper is that document: keep its code
                }
                if (!(await check({}))?.match) return setStep(typed.length <= 4 ? "full" : "key"); // last 4 first, then the whole code, whose key proves it
              }
              go(false, "");
            }}><ScanLine aria-hidden />{texts.scan}</Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={pass !== null}>
        <DialogContent showCloseButton={false}>
          <div role="status" className="flex flex-col items-center gap-4 py-4 text-center">
            <Loader2 className="size-8 animate-spin text-primary" aria-hidden />
            <DialogTitle>{texts.waiting}</DialogTitle>
            <DialogDescription>{texts.waitingHelp}</DialogDescription>
            <Button type="button" variant="outline" onClick={abandon}>{texts.abandon}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
